// src/Pages/Commerce/CommerceEmbeddedCheckoutCompletePage.js
//
// Where Stripe's embedded Checkout `return_url` sends the browser once the
// customer finishes on CommerceEmbeddedCheckoutPage.js. This checkout only
// accepts cards (see start_embedded_checkout()'s payment_method_types
// comment), which settle synchronously, and Stripe only invokes return_url
// after a completed payment attempt — so arrival here already implies the
// charge succeeded. We show success immediately rather than waiting on our
// own webhook (checkout.session.completed, handled server-side in
// app/webhooks/stripe_routes.py -> commerce_checkout_service.confirm_payment)
// to flip the session's status; a short background poll only exists to catch
// the rare case where the session later reads as failed (e.g. a refund/void).

import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, Paper, Stack, Button } from "@mui/material";
import { CheckCircle as CheckCircleIcon, Lock as LockIcon, ErrorOutline as ErrorIcon } from "@mui/icons-material";
import { alpha, keyframes } from "@mui/material/styles";
import { useQuery } from "react-query";
import client from "../../API";
import { COMMERCE_BRAND as BLITZ } from "./commerceShared.js";

const fadeUp = keyframes`from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}`;
const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;

const AUTO_REDIRECT_SECONDS = 4;
const POLL_INTERVAL_MS = 1500;
const MAX_POLLS = 8; // ~12s of background polling for a late status change to "failed"

function PageShell({ children }) {
  return (
    // overflow:hidden intentionally omitted — see CommerceEmbeddedCheckoutPage.js's
    // PageShell for why (no decorative overlay to clip here either, and
    // clipping+centering crops content instead of letting the page scroll).
    <Box sx={{
      minHeight: "100vh", position: "relative",
      background: `linear-gradient(160deg, ${BLITZ.skyDeep} 0%, ${BLITZ.sky} 55%, ${alpha(BLITZ.skyLight, 0.9)} 100%)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      py: { xs: 4, md: 8 }, px: 2,
    }}>
      <Box sx={{ width: "100%", maxWidth: 440, position: "relative", zIndex: 1, animation: `${fadeIn} 0.3s ease` }}>
        {children}
      </Box>
    </Box>
  );
}

function IconBadge({ bg, color, children }) {
  return (
    <Box sx={{
      width: 72, height: 72, borderRadius: "50%", bgcolor: bg, color,
      display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2.5,
    }}>
      {children}
    </Box>
  );
}

export default function CommerceEmbeddedCheckoutCompletePage() {
  const { sessionId, token } = useParams();
  const pollAttempts = useRef(0);

  const { data: session } = useQuery(
    ["commerce", "checkout-session-poll", sessionId, token],
    () => client.commerce.getCheckoutSession(sessionId, token),
    {
      retry: false,
      refetchInterval: (data) => {
        if (data?.status && data.status !== "awaiting_payment") return false;
        if (pollAttempts.current >= MAX_POLLS) return false;
        pollAttempts.current += 1;
        return POLL_INTERVAL_MS;
      },
    },
  );

  const status = session?.status;
  // success_url is present on the session regardless of status, so the
  // redirect target is known as soon as the first fetch resolves — it
  // doesn't need to wait for status to reach "paid".
  const returnTarget = session?.success_url || null;
  const [secondsLeft, setSecondsLeft] = useState(AUTO_REDIRECT_SECONDS);

  useEffect(() => {
    if (status === "failed" || !returnTarget) return undefined;
    if (secondsLeft <= 0) {
      window.location.href = returnTarget;
      return undefined;
    }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [status, returnTarget, secondsLeft]);

  if (status === "failed") {
    return (
      <PageShell>
        <Paper elevation={0} sx={{ borderRadius: 4, p: { xs: 3, md: 4 }, textAlign: "center", animation: `${fadeUp} 0.25s ease` }}>
          <IconBadge bg="#fef2f2" color="#dc2626">
            <ErrorIcon sx={{ fontSize: 40 }} />
          </IconBadge>
          <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
            Payment couldn't be completed
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You have not been charged. Please try again from the store.
          </Typography>
        </Paper>
      </PageShell>
    );
  }

  // Arrival at this page already implies the charge succeeded (see file-top
  // comment) — show success immediately rather than waiting for our status
  // poll to catch up.
  return (
    <PageShell>
      <Paper elevation={0} sx={{ borderRadius: 4, p: { xs: 3, md: 4 }, textAlign: "center", animation: `${fadeUp} 0.25s ease` }}>
        <IconBadge bg="#dcfce7" color="#16a34a">
          <CheckCircleIcon sx={{ fontSize: 40 }} />
        </IconBadge>
        <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
          Payment received
        </Typography>

        {returnTarget ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Thanks for your purchase! Redirecting you back in {secondsLeft}s…
            </Typography>
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={() => { window.location.href = returnTarget; }}
              sx={{ textTransform: "none", bgcolor: BLITZ.sky, "&:hover": { bgcolor: BLITZ.skyDark } }}
            >
              Continue now →
            </Button>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Thanks for your purchase — you're all set. The store has been notified.
          </Typography>
        )}
      </Paper>
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75} sx={{ mt: 2.5, opacity: 0.85 }}>
        <LockIcon sx={{ fontSize: 13, color: "white" }} />
        <Typography variant="caption" sx={{ color: "white", fontSize: 11 }}>
          Secured by Blitz
        </Typography>
      </Stack>
    </PageShell>
  );
}
